import type { IconButtonProps } from 'react-native-paper';
import { IconButton } from 'react-native-paper';

import { CloseIcon } from '@/assets';
import type { AppTheme } from '@/styles/theme';
import type { SVGComponent } from '@/types';

export type BottomModalEndButtonVariant = 'close' | 'confirm';

/**
 * Semantic end-button presets. Returns the styled IconButton props for a
 * variant so every BottomModal renders close/confirm consistently. Explicit
 * props passed by the caller override these (they are spread afterwards).
 */
export const variantButtonProps = (
  variant: BottomModalEndButtonVariant,
  theme: AppTheme
): Partial<IconButtonProps> => {
  switch (variant) {
    case 'close': {
      return {
        mode: 'outlined',
        iconColor: theme.colors.primary,
        theme: { colors: { outline: theme.colors.primary } }
      };
    }
    case 'confirm': {
      return {
        mode: 'contained',
        containerColor: theme.colors.error,
        iconColor: theme.colors.onPrimary
      };
    }
  }
};

export type BottomModalEndButton = {
  icon: SVGComponent;
  /**
   * Semantic styling preset applied before any explicit IconButton props
   * (which still win). `close` → blue outlined, `confirm` → red contained.
   * Prefer this over hand-rolling `mode` + `theme` overrides at the call site.
   */
  variant?: BottomModalEndButtonVariant;
  /**
   * if modal should be closed if button is pressed
   */
  shouldCloseWhenPressed?: boolean;
  /**
   * Position of custom buttons
   * Note: for now only if before or after close button
   */
  position?: 'before-close' | 'after-close';
} & Partial<IconButtonProps>;

type BottomModalEndButtonsProps = {
  showCloseButton: boolean;
  closeButtonMode?: IconButtonProps['mode'];
  offsetCloseButton: boolean;
  endButtons?: BottomModalEndButton[];
  onClose?: () => void;
  theme: AppTheme;
};

/**
 * Assembles the trailing button row of a BottomModal: the close button plus
 * any custom `endButtons`, honouring their `before-close`/`after-close`
 * positioning relative to the close button.
 */
export const BottomModalEndButtons = ({
  showCloseButton,
  closeButtonMode,
  offsetCloseButton,
  endButtons = [],
  onClose,
  theme
}: BottomModalEndButtonsProps) => {
  const buttonRow: React.ReactElement[] = [];

  if (showCloseButton) {
    buttonRow.push(
      <IconButton
        key="close-button"
        mode={closeButtonMode}
        style={offsetCloseButton ? { alignSelf: 'flex-end' } : undefined}
        icon={({ color, size }) => (
          <CloseIcon fill={color} width={size} height={size} />
        )}
        onPress={() => onClose?.()}
      />
    );
  }

  for (const [index, button] of endButtons.entries()) {
    const {
      icon,
      variant,
      shouldCloseWhenPressed,
      position,
      onPress,
      ...rest
    } = button;

    const insertIndex =
      position === 'after-close' || position === undefined
        ? buttonRow.length
        : 0;
    buttonRow.splice(
      insertIndex,
      0,
      <IconButton
        mode={closeButtonMode}
        key={`custom-button-${index}`}
        {...(variant ? variantButtonProps(variant, theme) : {})}
        icon={({ color, size }) =>
          icon({ fill: color, width: size, height: size })
        }
        onPress={(e) => {
          onPress?.(e);
          if (shouldCloseWhenPressed) onClose?.();
        }}
        {...rest}
      />
    );
  }

  return buttonRow;
};
